import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { RotateCw } from 'lucide-react'
import { useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import MarketStatusPill from '@/components/ui/MarketStatusPill'
import MenuSingle from '@/components/ui/MenuSingle'
import Pagination from '@/components/ui/Pagination'
import RefreshBar from '@/components/ui/RefreshBar'
import SelectTrigger from '@/components/ui/SelectTrigger'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import { useDepthSectors } from '@/features/depth/api/queries'
import { useLastClose } from '@/features/market/lib/useLastClose'
import { pricesKeys } from '@/features/prices/api/keys'
import { useLiveMarket } from '@/features/prices/api/queries'
import BreadthFilter from '@/features/prices/components/BreadthFilter'
import PriceTable from '@/features/prices/components/PriceTable'
import { useManualRefresh } from '@/hooks/useManualRefresh'
import { useOnline } from '@/hooks/useOnline'
import { useRefreshState } from '@/hooks/useRefreshState'
import { sentenceCase } from '@/lib/text'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import { cn } from '@/lib/utils'
import { LiveMarketBoardSkeleton } from '@/screens/market/components/LiveMarketBoardSkeleton'
import { loadLiveMarket, toLiveMarketQuery } from '@/screens/market/LiveMarketScreen.loader'
import { liveMarketQuerySchema, type LiveMarketPage } from '@/schemas/prices'
import type { PageSize } from '@/schemas/table'

/** The mock stacks the strip, the toolbar and the table 16 apart, not the page's 24. */
const STACK = 'gap-4'

/** Known without data, so the pending and failed pages draw the real one. */
const HEADER = <TitleRow title="Live Market" />

function LiveMarketPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading live market…"
      header={HEADER}
      pageTitle="Live Market"
      mainClassName={STACK}
    >
      <LiveMarketBoardSkeleton />
    </ScreenLayout>
  )
}

/**
 * The copy the page used for a failed load before it had a loader —
 * `EmptyState kind="failed" title="Couldn't load live market"` — carried
 * forward onto the route's own error page. Offline copy is automatic
 * (`OfflineError`, see RouteErrorPage).
 */
function LiveMarketError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Couldn't load live market" />
}

/**
 * `liveMarketQuerySchema` (schemas/prices.ts) already defaults page/pageSize
 * and leaves sector/breadth optional, so it validates a bare `/market/live`
 * on its own — no local extension needed the way 52-week and Depth require.
 */
export const Route = createFileRoute('/_app/market/live')({
  validateSearch: liveMarketQuerySchema,
  loader: loadLiveMarket,
  pendingComponent: LiveMarketPending,
  errorComponent: LiveMarketError,
  component: LiveMarketScreen,
})

const ALL_SECTORS = { value: '', label: 'All sectors' }

/**
 * The round Refresh (the mock's `mkc-refresh`): the secondary ink on the inset fill, lighting to
 * the primary on the raised one, and its glyph turning once per press (0.6s ease-out). Re-keyed
 * each click so a second press turns it again; reduced motion takes the turn away.
 */
function RefreshButton({ onClick }: { onClick: () => void }) {
  const [presses, setPresses] = useState(0)
  return (
    <button
      type="button"
      aria-label="Refresh prices"
      onClick={() => {
        setPresses((count) => count + 1)
        onClick()
      }}
      className="inline-flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-popover hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring lg:size-9"
    >
      <RotateCw
        key={presses}
        className={cn('size-5', presses > 0 && 'animate-spin-once')}
        aria-hidden="true"
      />
    </button>
  )
}

/**
 * Live market — the price table read once, as `useLiveMarket` keyed on the
 * whole search query. `keepPreviousData` keeps `query.data` defined WHILE a
 * filter, sort or page change's new key is pending, and a background poll
 * failing after that keeps the last rows on screen, RefreshBar saying so —
 * but a key change that itself fails (a fresh filter with nothing cached
 * under it, three retries later) leaves `query.data` genuinely `undefined`,
 * same as any other failed query. The header, the sector menu and Refresh
 * stay mounted regardless (they read `search`/`sectors.data`, never
 * `query.data`); only the status line and the table below branch on the
 * load, the same region-local shape `StockPricesScreen` uses for its own
 * search-keyed main read.
 *
 * `search` is re-parsed through `toLiveMarketQuery` rather than read
 * straight off `Route.useSearch()`: the router merges the URL's raw query
 * string into that object, so an unrecognised param would otherwise key
 * `useLiveMarket` here off a different object than the one the loader
 * warmed for the entry URL — see the loader's own comment.
 */
function LiveMarketScreen() {
  const search = toLiveMarketQuery(Route.useSearch())
  const navigate = Route.useNavigate()
  const online = useOnline()
  const query = useLiveMarket(search)
  const sectors = useDepthSectors()
  // Drives BOTH the Refresh button below and the Retry inside RefreshBar, and
  // not `query.refetch()` on its own: this screen's `queryFn` fans out through
  // the endpoint cache, where a bare refetch inside the 10s freshness window
  // returns the entry it already has and reaches no network at all — a Refresh
  // click four seconds after a poll tick re-announced identical prices as
  // "Updated just now". `useManualRefresh` invalidates those entries first, and
  // carries the "a member asked for this" flag `useRefreshState` needs to tell a
  // click from livePolling's interval. See its own doc comment.
  const manual = useManualRefresh([query])
  const refresh = useRefreshState([query], manual.isManual)
  const [sectorOpen, setSectorOpen] = useState(false)

  const lastClose = useLastClose<LiveMarketPage>(pricesKeys.liveMarketTables(), query.data)
  const data = lastClose.data
  const view = lastClose.isShowingSaved ? 'loaded' : resolveViewState(query, { online })
  const sectorOptions = [
    ALL_SECTORS,
    ...(sectors.data ?? []).map((s) => ({ value: s.id, label: sentenceCase(s.name) })),
  ]
  const barState = lastClose.isShowingSaved ? 'offline' : refresh.state
  const sector = search.sector ?? ''
  const sectorLabel = sectorOptions.find((s) => s.value === sector)?.label ?? ALL_SECTORS.label

  return (
    <ScreenLayout header={HEADER} pageTitle="Live Market" mainClassName={STACK}>
      {/* The toolbar (the mock's): the three breadth chips (drawn once the first response has the
          counts), the Market open/closed pill with its last-close tooltip, the sector menu and
          Refresh. The pill is the app's own `MarketStatusPill`, the one the top bar wears: the
          mock draws the state beside the table it describes, and the top bar's pill stays. */}
      <div className="flex flex-wrap items-center gap-4">
        {data ? (
          <BreadthFilter
            breadth={data.breadth}
            value={search.breadth}
            className="min-w-0 flex-1 basis-80"
            onChange={(value) => {
              void navigate({ search: (prev) => ({ ...prev, breadth: value, page: 1 }) })
            }}
          />
        ) : (
          <div className="flex-1" />
        )}
        <MarketStatusPill />
        <div className="flex min-w-0 items-center gap-2">
          {/* An empty string is the absence of a filter, not a value: the menu
              keeps it so the list has a row to point at, and `|| undefined`
              drops `sector` out of the URL again on the way back. `max-w-full`
              because the trigger is `shrink-0` and a long sector name would
              otherwise push this row past the column. Kept mounted whether or
              not the table's own query has data — it reads `sectors.data` and
              `search`, neither of which this screen's own failure touches. The
              menu is the mock's `mkc-menu`: 12px rows, the current one white at 600. */}
          <MenuSingle
            options={sectorOptions}
            value={sector}
            onChange={(value) => {
              void navigate({
                search: (prev) => ({ ...prev, sector: value || undefined, page: 1 }),
              })
            }}
            open={sectorOpen}
            onOpenChange={setSectorOpen}
            label="Sector"
            searchable={false}
            align="end"
            skin="plain"
            className="w-44"
            trigger={(triggerProps) => (
              <SelectTrigger
                {...triggerProps}
                className="max-w-full text-body-m"
                aria-label={`Filter by sector, ${sectorLabel}`}
                label={sectorLabel}
                open={sectorOpen}
              />
            )}
          />
          <RefreshButton onClick={manual.refresh} />
        </div>
      </div>

      {/* Only a state worth interrupting for: a manual refresh in flight, a
          failed poll, offline. The quiet "Updated just now" of a healthy poll
          is not drawn — it was a permanent 40px band above the table, and the
          Refresh button and the as-of time already say the prices are live. */}
      {barState === null || barState === 'updated' ? null : (
        <RefreshBar
          state={barState}
          {...(refresh.message === undefined ? {} : { message: refresh.message })}
          onRetry={manual.refresh}
        />
      )}

      {view === 'failed' || view === 'offline' ? (
        // A key change (filter/sort/page) that never lands anything —
        // `keepPreviousData` had nothing to hold once the fetch itself
        // failed, so `data` is undefined here exactly as it is for any other
        // failed query. The route's own error page (LiveMarketError) is only
        // for the load that never lands at all on first entry; this is the
        // region-local counterpart for a search-param change, same shape
        // `StockPricesScreen`/`Week52Screen` use for theirs.
        <EmptyState
          kind={view}
          size="page"
          title={view === 'offline' ? undefined : "Couldn't load live market"}
          onRetry={manual.refresh}
          retrying={manual.isManual}
          meta={view === 'failed' ? lastUpdatedMeta(query.dataUpdatedAt) : undefined}
          savedAt={lastClose.savedAt}
          onShowLastClose={lastClose.showLastClose}
        />
      ) : data === undefined ? (
        // Only reachable before the route's loader has ever resolved once,
        // which it always has by the time this mounts, or in the instant
        // between a key change starting and `keepPreviousData` having
        // nothing of its own to hold — in practice never either, but
        // `useQuery`'s type cannot see that guarantee.
        <Skeleton className="h-150 w-full" />
      ) : (
        <>
          {/* Table and pager travel as ONE block of the stack: `Pagination` draws
              its own 24px (see its PLACEMENT note) and a parent `gap-*` would add
              to it rather than absorb it. */}
          <div>
            <PriceTable
              rows={data.rows}
              variant="live"
              isLoading={query.isLoading}
              pageSize={search.pageSize}
              sortBy={search.sortBy}
              sortDesc={search.sortDesc}
              onSortChange={(sortBy, sortDesc) => {
                void navigate({
                  search: (prev) => ({
                    ...prev,
                    sortBy,
                    sortDesc: sortBy === undefined ? undefined : sortDesc,
                    page: 1,
                  }),
                })
              }}
            />

            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              // `/market/live/` takes a `limit` and no `page`; at `LIVE_MAX_LIMIT`
              // it serves every company that trades, so `total` is the market.
              unit="companies"
              onPageChange={(page) => {
                void navigate({ search: (prev) => ({ ...prev, page }) })
              }}
              onPageSizeChange={(pageSize) => {
                void navigate({
                  search: (prev) => ({ ...prev, pageSize: pageSize as PageSize, page: 1 }),
                })
              }}
            />
          </div>
        </>
      )}
    </ScreenLayout>
  )
}
